/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import {
    Component as ReactComponent,
    type ErrorInfo,
    type ReactElement,
    type ReactNode,
    memo,
    Suspense,
    useEffect,
} from 'react';
import { registry } from '@/lib/page-designer/registry';
import { Await, useAsyncError } from 'react-router';
import { createLogger } from '@/lib/logger';

const logger = createLogger();
import type { ComponentDesignMetadata } from '@salesforce/storefront-next-runtime/design/react';
import { useComponentDataById } from './component-data-context';
// RGBARRY-CUSTOMIZATION: keep only actually server-rendered critical components boundary-free.
import {
    CriticalComponentHydrationMarker,
    useIsInCriticalRegion,
    useWasServerRendered,
} from './critical-component-context';
// END RGBARRY-CUSTOMIZATION
import type { ComponentType } from './index';

export interface ComponentProps {
    component: ComponentType;
    className?: string;
    /** Props supplied by a containing component to specialize a nested Page Designer component. */
    componentProps?: Record<string, unknown>;
    regionId: string;
}

interface ResolvedComponentProps extends ComponentProps {
    data: unknown;
    designMetadata: ComponentDesignMetadata;
}

interface ComponentErrorBoundaryProps {
    children: ReactNode;
    componentId: string;
    componentTypeId: string;
    fallback: ReactNode;
}

interface ComponentErrorBoundaryState {
    error: Error | null;
}

/** Keep client-side Page Designer component failures local to the affected component. */
class ComponentErrorBoundary extends ReactComponent<ComponentErrorBoundaryProps, ComponentErrorBoundaryState> {
    state: ComponentErrorBoundaryState = { error: null };

    static getDerivedStateFromError(error: Error): ComponentErrorBoundaryState {
        return { error };
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
        registry.clearRegistrationError(this.props.componentTypeId);
        logger.error(
            `Failed to render Page Designer component "${this.props.componentId}" (${this.props.componentTypeId})`,
            {
                error,
                errorInfo,
            }
        );
    }

    render(): ReactNode {
        return this.state.error ? this.props.fallback : this.props.children;
    }
}

/**
 * Error handler component that logs component data loading errors and renders nothing.
 * Uses React Router's useAsyncError to access the error from the Await boundary.
 *
 * When a component's data fails to load (e.g., API error), we render nothing (null)
 * instead of showing a misleading skeleton/fallback. The error is logged to the console
 * for debugging purposes.
 */
function ComponentErrorFallback({ componentId, componentTypeId }: { componentId: string; componentTypeId: string }) {
    const error = useAsyncError();

    useEffect(() => {
        logger.error(`Failed to load data for component "${componentId}" (${componentTypeId})`, { error });
    }, [componentId, componentTypeId, error]);

    // Render nothing when data loading fails
    return null;
}

/**
 * Resolves the concrete Page Designer component inside the component-local Suspense boundary.
 * Keeping the potentially suspending registry lookup here gives SSR and hydration the same
 * boundary structure, so a cold client registry cannot replace the whole parent region.
 */
function ResolvedComponent({
    component,
    className,
    componentProps,
    regionId,
    data,
    designMetadata,
}: ResolvedComponentProps) {
    if (!registry.hasConcreteComponent(component.typeId)) {
        const registrationError = import.meta.env.SSR
            ? registry.consumeRegistrationError(component.typeId)
            : registry.getRegistrationError(component.typeId);
        if (registrationError) throw registrationError;
        // oxlint-disable-next-line @typescript-eslint/only-throw-error
        throw registry.loadAndRegister(component.typeId);
    }
    const DynamicComponent = registry.getComponent(component.typeId);
    if (!DynamicComponent) throw new Error(`Registered Page Designer component "${component.typeId}" is unavailable`);

    return (
        <DynamicComponent
            {...(component.data ?? {})}
            {...componentProps}
            designMetadata={designMetadata}
            component={component}
            data={data}
            className={className}
            regionId={regionId}
        />
    );
}

export const Component = memo(function Component({
    component,
    className,
    componentProps,
    regionId,
}: ComponentProps): ReactElement {
    // Get this component's data promise from context by its ID
    const dataPromise = useComponentDataById(component.id);
    // RGBARRY-CUSTOMIZATION: keep only actually server-rendered critical components boundary-free.
    const isInCriticalRegion = useIsInCriticalRegion();
    const wasServerRendered = useWasServerRendered(component.id, component.typeId);
    // END RGBARRY-CUSTOMIZATION
    const FallbackComponent = registry.getFallback(component.typeId);

    const designMetadata: ComponentDesignMetadata = {
        name: component.designMetadata?.name,
        isFragment: Boolean(component.fragment),
        // RGBARRY-CUSTOMIZATION: nested Page Designer components can omit the optional visibility
        // flag when they use the default visible state. Preserve an explicit false, but do not turn
        // an omitted value into false or the design decorator hides and greys out the component.
        isVisible: component.visible ?? component.designMetadata?.isVisible ?? true,
        // END RGBARRY-CUSTOMIZATION
        isLocalized: Boolean(component.localized),
        id: component.id,
        // In content block editor a standalone block has no contentLinkUuid.
        // Fall back to the component id so selection/hover identity is never the empty
        // string, which would otherwise collide with the '' default and render the block
        // permanently selected. Matches the `?? id` fallback used in region-wrapper/index.
        contentLinkUuid: component.contentLinkUuid ?? component.id,
    };

    const renderComponent = (data: unknown) => (
        <ResolvedComponent
            designMetadata={designMetadata}
            component={component}
            data={data}
            className={className}
            componentProps={componentProps}
            regionId={regionId}
        />
    );

    const fallback = FallbackComponent ? <FallbackComponent {...(component.data ?? {})} /> : <div />;

    const content =
        dataPromise instanceof Promise ? (
            <Await
                resolve={dataPromise}
                errorElement={<ComponentErrorFallback componentId={component.id} componentTypeId={component.typeId} />}>
                {renderComponent}
            </Await>
        ) : (
            renderComponent(undefined)
        );

    // RGBARRY-CUSTOMIZATION: A critical component without deferred data must block the server shell when its module or
    // render tree suspends. A local Suspense boundary would flush the empty component fallback and
    // move its SSR content into a script-dependent hidden streaming segment instead.
    const componentContent =
        isInCriticalRegion && wasServerRendered && !(dataPromise instanceof Promise) ? (
            content
        ) : (
            <Suspense fallback={fallback}>{content}</Suspense>
        );
    // END RGBARRY-CUSTOMIZATION

    return (
        <>
            {wasServerRendered && (
                <CriticalComponentHydrationMarker componentId={component.id} componentTypeId={component.typeId} />
            )}
            <ComponentErrorBoundary
                key={`${component.id}:${component.typeId}`}
                componentId={component.id}
                componentTypeId={component.typeId}
                fallback={fallback}>
                {componentContent}
            </ComponentErrorBoundary>
        </>
    );
});
